import type { ProductLine } from "@adpilot/shared";
import { useTranslation } from "react-i18next";
import { FilterSelect } from "@/components/patterns/filter-select";
import { SearchInput } from "@/components/patterns/SearchInput";
import { ProductLineFilter } from "./ProductLineFilter";

export interface ProductCenterFilters {
  active: "true" | "false" | "all";
  productStatus: "all" | "advertised" | "unadvertised";
}

export function ProductToolbar({
  search,
  filters,
  productLines,
  isProductLinesLoading,
  isProductLinesError,
  productLineIds,
  onSearchChange,
  onFiltersChange,
  onProductLineChange,
  onRetryProductLines,
}: {
  search: string;
  filters: ProductCenterFilters;
  productLines: ProductLine[];
  isProductLinesLoading: boolean;
  isProductLinesError: boolean;
  productLineIds: string[];
  onSearchChange: (value: string) => void;
  onFiltersChange: (filters: ProductCenterFilters) => void;
  onProductLineChange: (productLineIds: string[]) => void;
  onRetryProductLines: () => void;
}): React.ReactElement {
  const { t } = useTranslation("productCenter");
  const listingItems = [
    { value: "all", label: t("toolbar.allListings") },
    { value: "true", label: t("toolbar.active") },
    { value: "false", label: t("toolbar.inactive") },
  ];
  const adsItems = [
    { value: "all", label: t("toolbar.allAdsStates") },
    { value: "advertised", label: t("toolbar.advertised") },
    { value: "unadvertised", label: t("toolbar.unadvertised") },
  ];
  return (
    <div className="flex min-w-0 flex-1 flex-wrap items-center justify-between gap-2">
      <SearchInput
        showIcon
        className="w-[300px]"
        value={search}
        onChange={(event) => onSearchChange(event.currentTarget.value)}
        placeholder={t("toolbar.searchPlaceholder")}
        aria-label={t("toolbar.searchAria")}
      />
      <div className="flex shrink-0 items-center gap-2">
        <ProductLineFilter
          productLines={productLines}
          isLoading={isProductLinesLoading}
          isError={isProductLinesError}
          selected={productLineIds}
          onChange={onProductLineChange}
          onRetry={onRetryProductLines}
        />
        <FilterSelect
          ariaLabel={t("toolbar.listingStateAria")}
          items={listingItems}
          value={filters.active}
          onValueChange={(value) =>
            onFiltersChange({ ...filters, active: value as ProductCenterFilters["active"] })
          }
        />
        <FilterSelect
          ariaLabel={t("toolbar.adsStateAria")}
          items={adsItems}
          value={filters.productStatus}
          onValueChange={(value) =>
            onFiltersChange({
              ...filters,
              productStatus: value as ProductCenterFilters["productStatus"],
            })
          }
        />
      </div>
    </div>
  );
}
